import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { PageLoader } from '@/layouts/components/PageLoader/PageLoader';
import { useUser } from '@/features/user/hooks/useUser';
import { useOnboarding } from '@/features/onboarding';
import { useKyc } from '@/features/kyc';
import {
  getScoringStep,
  ScoringStep,
  useScoring,
  type ScoringState,
} from '@/features/scoring';
import { routes } from '../routes';

// Declared income, BC consent and the BC score come before KYC so a
// preliminary offer can be shown.
export const ScoringGuard = () => {
  const location = useLocation();

  const { user } = useUser();
  const { onboardingState, isOnboardingStateLoading } = useOnboarding(true);
  const { isKycLoading, isKycValid } = useKyc(true);
  const { scoringState, isScoringStateLoading } = useScoring({
    fetchScoringState: true,
  });

  if (isOnboardingStateLoading || isKycLoading || isScoringStateLoading) {
    return <PageLoader />;
  }

  const nextStep = getNextScoringStep(
    !!user?.declaredIncome,
    !!onboardingState?.isBcTermsAccepted,
    scoringState,
    isKycValid
  );

  // The code page follows the phone choice; the consent page is reachable
  // again when the backend reports the consent missing.
  const isFreeRoute =
    (location.pathname === routes.scoring.code &&
      nextStep === routes.scoring.phones) ||
    (location.pathname === routes.scoring.terms &&
      nextStep === routes.scoring.base);

  if (location.pathname !== nextStep && !isFreeRoute) {
    return <Navigate to={nextStep} replace />;
  }

  return <Outlet />;
};

const getNextScoringStep = (
  hasDeclaredIncome: boolean,
  isBcTermsAccepted: boolean,
  scoringState: ScoringState | undefined,
  isKycValid: boolean
) => {
  if (!hasDeclaredIncome) return routes.scoring.income;
  if (!isBcTermsAccepted) return routes.scoring.terms;

  switch (getScoringStep(scoringState, isKycValid)) {
    case ScoringStep.Done:
      return routes.wizard.base;
    case ScoringStep.PhoneVerification:
      return routes.scoring.phones;
    default:
      return routes.scoring.base;
  }
};
